import React, { useEffect, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import {
  VirtualTourEmbed as BaseVirtualTourEmbed,
  contentSchema as baseSchema,
} from '@vibesites/theme-preferred/components/VirtualTourEmbed';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Dietz override of the parent VirtualTourEmbed.
 *
 * Wraps the parent component unchanged (the empty-state UI it ships with is
 * already what we want) and adds a runtime side-effect: when this property
 * has zero virtual tours with a usable URL, inject
 * `<meta name="robots" content="noindex, nofollow">` into the document head
 * so the empty Virtual Tour page is excluded from search results.
 *
 * Why client-side: the static SSG noindex pipeline reads the `noindex` flag
 * from pages.json, which is property-agnostic. We want the noindex to vary
 * per property based on `property.virtual_tours`, so it has to be applied
 * at runtime. Googlebot executes JS and honors dynamically-injected robots
 * meta tags; this is the standard SPA pattern for property-conditional SEO.
 *
 * Pairs with the dynamic nav-item filtering in Header.tsx and
 * SitemapContent.tsx so users with no tours never reach this page from
 * within the site, and crawlers that follow external links get noindex'd.
 *
 * We read the same binding path the parent VirtualTourEmbed uses
 * (`virtualtourembed.tours`) — both reads are deduped through the runtime
 * binding system and editor overrides apply uniformly. Using the legacy
 * `useVirtualTours()` hook here would trip the schema linter's
 * legacy-collection-hook-with-binding rule because our re-attached
 * `baseSchema` declares this binding.
 */
export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });
  const hasTours = useMemo(
    () => Array.isArray(tours) && tours.some((t) => t && t.virtual_tour_url),
    [tours],
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (hasTours) return;

    const head = document.head;
    let meta = head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    let createdByUs = false;
    let previousContent = '';
    if (meta) {
      previousContent = meta.content;
    } else {
      meta = document.createElement('meta');
      meta.name = 'robots';
      head.appendChild(meta);
      createdByUs = true;
    }
    meta.content = 'noindex, nofollow';

    return () => {
      if (createdByUs) {
        meta!.remove();
      } else if (meta) {
        meta.content = previousContent;
      }
    };
  }, [hasTours]);

  return <BaseVirtualTourEmbed />;
}

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = baseSchema;

export default VirtualTourEmbed;
